import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import type { PerformanceUnit, ReportingOptions } from "../../../types/stationPerformance";
import { useMonthlyPerformance } from "../hooks/useMonthlyPerformance";
import MeasureValueCell from "./MeasureValueCell";
import DeltaCell from "./DeltaCell";
import ChangeBandLegend from "./ChangeBandLegend";
import FreeShareCell from "./FreeShareCell";
import { COCHARGE_REVENUE_TEXTS, PROJECTION_TEXTS, projectionHover } from "../StationPerformance.consts";
import DefinitionContextBanner from "./DefinitionContextBanner";
import NeverComputedNotice from "./NeverComputedNotice";
import UnitReconciliationPanel from "./UnitReconciliationPanel";
import ExportCsvButton from "./ExportCsvButton";
import { cell, measureCell, measureReasonCell, textCell } from "../utils/csv";
import { useUnitReconciliation } from "../hooks/useUnitReconciliation";
import { berlinYearMonth, projectToMonthEnd } from "../utils/projection";

interface StationLeagueTableProps {
  month: string;
  options: ReportingOptions;
  onSelectUnit: (unitId: string) => void;
  /** Injectable for tests; the projections count days on the Berlin calendar of this moment. */
  now?: Date;
}

const euro = (cents: number) =>
  (cents / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" });

/**
 * The monthly league table (US1, US2, US7).
 *
 * Each row carries its actuals, the change on the previous month, and the station's own baseline: so a
 * reader can tell not just whether a month was good, but whether it was good *for that station*. The
 * network average sits above the table as the other reference point; the two routinely disagree, and that
 * disagreement is the finding rather than noise.
 */
const StationLeagueTable: React.FC<StationLeagueTableProps> = ({ month, options, onSelectUnit, now }) => {
  const [sort, setSort] = useState("moneyCents");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const { report, fetchedAt, initialLoading, refreshing, neverComputed, error, fetchReport } =
    useMonthlyPerformance({ month, options, sort, order, limit: 200 });
  // Fetched regardless of the reader's current unit: the point of the panel is to show both bases at
  // once, so it must not depend on which one happens to be selected.
  const { reconciliation } = useUnitReconciliation({ month, options });

  if (initialLoading) {
    return (
      <div className="flex justify-content-center p-5">
        <ProgressSpinner />
      </div>
    );
  }
  if (neverComputed) return <NeverComputedNotice months={neverComputed} onComputed={fetchReport} />;
  if (error) return <Message severity="error" text={error} />;
  if (!report) return <Message severity="info" text="Nothing to show for this period." />;

  const network = report.networkAverage;

  // Projected columns exist only while the month is OPEN (and is the current Berlin month, which the
  // helper checks). Sealed and grace months are final or nearly so: nothing to project.
  // The moment the report was read, so the projection counts today only as far as the figures do.
  const at = now ?? fetchedAt ?? new Date();
  const projecting = report.context?.monthClosure?.[month] === "OPEN" && month === berlinYearMonth(at);
  const liveDayIncluded = Boolean(report.context?.liveDayIncluded);
  const projectValue = (value: number | null | undefined) =>
    projecting ? projectToMonthEnd(value, at, month, liveDayIncluded) : null;
  const projectedCell = (value: number | null | undefined, format: (v: number) => string) => {
    const projection = projectValue(value);
    return projection ? (
      <span className="text-color-secondary" title={projectionHover(projection, liveDayIncluded)}>
        {format(projection.value)}
      </span>
    ) : (
      <span className="text-color-secondary">
        {projecting && value !== null && value !== undefined && !Number.isNaN(value) ? PROJECTION_TEXTS.noData : "—"}
      </span>
    );
  };
  const moneyHeader = options.moneyView === "COCHARGE_SHARE" ? COCHARGE_REVENUE_TEXTS.shareLabel : "Money";
  const projectedNumber = (value: number | null | undefined, decimals: number) => {
    const projection = projectValue(value);
    return cell(projection ? Number(projection.value.toFixed(decimals)) : "");
  };

  return (
    <div>
      <DefinitionContextBanner context={report.context} />

      <UnitReconciliationPanel month={month} reconciliation={reconciliation} />

      <Card className="mb-3">
        <div className="flex flex-wrap gap-4 align-items-center">
          <span className="font-semibold">
            Network average
            <span
              className="text-color-secondary font-normal ml-2"
              title="Averaged across units that were active this month. Idle units are excluded, since including them would understate the typical working unit."
            >
              ({network?.basisCount ?? 0} active {options.unit === "SITE" ? "sites" : "connectors"})
            </span>
          </span>
          <span>
            Sessions <MeasureValueCell measure={network?.sessions} decimals={1} />
          </span>
          <span>
            Energy <MeasureValueCell measure={network?.energyKwh} decimals={0} suffix=" kWh" />
          </span>
          <span>
            Money <MeasureValueCell measure={network?.moneyCents} format="money" />
          </span>
          <span>
            Per session <MeasureValueCell measure={network?.moneyPerSessionCents} format="money" />
          </span>
          <span>
            Free share <MeasureValueCell measure={network?.freeShareOfEnergyPercent} format="percent" decimals={1} />
          </span>
        </div>
      </Card>

      <ChangeBandLegend />

      <div className="flex justify-content-between align-items-center mb-2">
        {refreshing ? (
          <span className="text-color-secondary text-sm">Refreshing…</span>
        ) : (
          <span />
        )}
        <ExportCsvButton
          filename={`station-performance-${month}.csv`}
          title={`Station performance — ${month}`}
          context={report.context}
          headers={[
            "Unit", "Station", "Connector", "Active connectors",
            "Sessions", "Paid sessions", "Free sessions", "Zero-energy sessions", "Qualifying sessions",
            "Energy kWh", "Paid energy kWh", "Free energy kWh",
            "Money cents", `${COCHARGE_REVENUE_TEXTS.shareLabel} cents`, "Owner cents",
            "kWh per session", "kWh per session n/a reason",
            "Money cents per session", "Money per session n/a reason",
            "Money cents per paid session", "Money per paid session n/a reason",
            "Free share of energy %", "Free share of sessions %",
            "Sessions MoM %", "Money MoM %",
            ...(projecting ? ["Sessions (projected)", "Energy kWh (projected)", "Money cents (projected)", "Free energy kWh (projected)"] : []),
          ].map(cell)}
          rows={report.units.map((u) => [
            textCell(u.unitId), textCell(u.stationName), textCell(u.evseId ?? ""), cell(u.activeConnectors),
            cell(u.sessions), cell(u.paidSessions), cell(u.freeSessions),
            cell(u.zeroEnergySessions), cell(u.qualifyingSessions),
            cell(u.energyKwh), cell(u.paidEnergyKwh), cell(u.freeEnergyKwh),
            cell(u.moneyCents), cell(u.cochargeMoneyCents), cell(u.ownerMoneyCents),
            measureCell(u.energyPerSession), measureReasonCell(u.energyPerSession),
            measureCell(u.moneyPerSessionCents), measureReasonCell(u.moneyPerSessionCents),
            measureCell(u.moneyPerPaidSessionCents), measureReasonCell(u.moneyPerPaidSessionCents),
            measureCell(u.freeShareOfEnergyPercent), measureCell(u.freeShareOfSessionsPercent),
            cell(u.monthOverMonth?.sessions?.percent ?? ""),
            cell(u.monthOverMonth?.moneyCents?.percent ?? ""),
            ...(projecting
              ? [projectedNumber(u.sessions, 0), projectedNumber(u.energyKwh, 1), projectedNumber(u.moneyCents, 0), projectedNumber(u.freeEnergyKwh, 1)]
              : []),
          ])}
        />
      </div>

      <DataTable
        value={report.units}
        dataKey="unitId"
        paginator
        rows={25}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sortField={sort}
        sortOrder={order === "desc" ? -1 : 1}
        onSort={(e) => {
          setSort(e.sortField);
          setOrder(e.sortOrder === -1 ? "desc" : "asc");
        }}
        removableSort
        selectionMode="single"
        onSelectionChange={(e) => onSelectUnit((e.value as PerformanceUnit)?.unitId)}
        emptyMessage="No units were active in this period."
        size="small"
        scrollable
      >
        <Column field="stationName" header="Station" sortable frozen style={{ minWidth: "12rem" }} />
        {options.unit === "CONNECTOR" && (
          <Column field="evseId" header="Connector" style={{ minWidth: "9rem" }} />
        )}
        {options.unit === "SITE" && (
          <Column
            field="activeConnectors"
            header="Conn."
            style={{ minWidth: "5rem" }}
            body={(u: PerformanceUnit) => (
              <span title="Active connectors. A multi-connector site is not comparable to a single-connector one.">
                {u.activeConnectors}
              </span>
            )}
          />
        )}
        <Column field="sessions" header="Sessions" sortable style={{ minWidth: "7rem" }} />
        {projecting && (
          <Column
            header="Sessions (projected)"
            style={{ minWidth: "9rem" }}
            body={(u: PerformanceUnit) =>
              projectedCell(u.sessions, (v) => Math.round(v).toLocaleString())
            }
          />
        )}
        <Column
          header="Δ Sessions"
          style={{ minWidth: "8rem" }}
          body={(u: PerformanceUnit) => <DeltaCell change={u.monthOverMonth?.sessions} />}
        />
        <Column
          field="energyKwh"
          header="Energy"
          sortable
          style={{ minWidth: "8rem" }}
          body={(u: PerformanceUnit) => `${u.energyKwh?.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`}
        />
        {projecting && (
          <Column
            header="Energy (projected)"
            style={{ minWidth: "9rem" }}
            body={(u: PerformanceUnit) =>
              projectedCell(u.energyKwh, (v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`)
            }
          />
        )}
        <Column
          header="Δ Energy"
          style={{ minWidth: "8rem" }}
          body={(u: PerformanceUnit) => <DeltaCell change={u.monthOverMonth?.energyKwh} />}
        />
        <Column
          field="moneyCents"
          header={options.moneyView === "COCHARGE_SHARE" ? COCHARGE_REVENUE_TEXTS.shareLabel : "Money"}
          sortable
          style={{ minWidth: "8rem" }}
          body={(u: PerformanceUnit) => euro(u.moneyCents)}
        />
        {projecting && (
          <Column
            header={`${moneyHeader} (projected)`}
            style={{ minWidth: "9rem" }}
            body={(u: PerformanceUnit) => projectedCell(u.moneyCents, euro)}
          />
        )}
        {projecting && (
          <Column
            header="Free energy (projected)"
            style={{ minWidth: "9rem" }}
            body={(u: PerformanceUnit) =>
              projectedCell(u.freeEnergyKwh, (v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`)
            }
          />
        )}
        <Column
          header="Δ Money"
          style={{ minWidth: "8rem" }}
          body={(u: PerformanceUnit) => <DeltaCell change={u.monthOverMonth?.moneyCents} />}
        />
        <Column
          field="energyPerSession"
          header="kWh / session"
          sortable
          style={{ minWidth: "9rem" }}
          body={(u: PerformanceUnit) => <MeasureValueCell measure={u.energyPerSession} decimals={1} />}
        />
        <Column
          field="moneyPerSessionCents"
          header="€ / session"
          sortable
          style={{ minWidth: "9rem" }}
          body={(u: PerformanceUnit) => <MeasureValueCell measure={u.moneyPerSessionCents} format="money" />}
        />
        <Column
          field="freeShareOfEnergyPercent"
          header="Free share"
          sortable
          style={{ minWidth: "9rem" }}
          body={(u: PerformanceUnit) => (
            <FreeShareCell
              energyShare={u.freeShareOfEnergyPercent}
              sessionShare={u.freeShareOfSessionsPercent}
            />
          )}
        />
        <Column
          header="Own baseline"
          style={{ minWidth: "10rem" }}
          body={(u: PerformanceUnit) => (
            <span
              className="text-color-secondary"
              title={`This unit's own mean across ${u.ownBaseline?.basisCount ?? 0} active month(s) — not the fleet average`}
            >
              <MeasureValueCell measure={u.ownBaseline?.sessions} decimals={1} /> sessions
            </span>
          )}
        />
        <Column
          header=""
          style={{ minWidth: "8rem" }}
          body={(u: PerformanceUnit) =>
            u.freeChargeDominated ? (
              <Tag
                value="all free"
                severity="info"
                title="Every session here was free charging, so €0 is a pricing choice rather than a failure"
              />
            ) : null
          }
        />
      </DataTable>
    </div>
  );
};

export default StationLeagueTable;
